import {
  FAShippingMethodChoice,
  FAStoreOrderSteps,
  StoreOrderSteps,
} from '../../../../../services/hookService/getOrderDetails/helper';
import {ShippingMethodTypes} from '../../../../../services/hookService/getOrderDetails/types';
import InfoRow from '../../../../shopping-basket/ItemCard/InfoRow';

interface IProps {
  storeOrderStep: StoreOrderSteps;
  hasOrderShipped?: boolean;
  deliveryDate: string | null;
  storeDeliveryMethod?: ShippingMethodTypes;
  acceptDate?: string;
  tracingCode?: string;
  receiptImage?: string;
  approved_at: string | null;
  isDeliveryInPerson: boolean;
}

// ==================== HELPER FUNCTIONS ====================

const getStatusDisplay = (storeOrderStep: StoreOrderSteps): string => {
  const isShippedOrDelivered =
    storeOrderStep === StoreOrderSteps.SHIPPED ||
    storeOrderStep === StoreOrderSteps.STORE_DELIVERED;

  return isShippedOrDelivered ? 'ارسال شده' : FAStoreOrderSteps[storeOrderStep];
};

const getDeliveryMethodText = (
  storeDeliveryMethod?: ShippingMethodTypes,
): string => {
  if (!storeDeliveryMethod) return 'مشخص نشده';
  return `${FAShippingMethodChoice[storeDeliveryMethod]} - هزینه بر عهده خریدار`;
};

// ==================== MINI COMPONENTS ====================

interface InfoRowConditionalProps {
  condition?: boolean;
  icon: string;
  label: string;
  value: string | null | undefined;
}

const ConditionalInfoRow = ({
  condition,
  icon,
  label,
  value,
}: InfoRowConditionalProps) => {
  if (!condition || !value) return null;

  return <InfoRow icon={icon} label={label} value={value} />;
};

interface ReceiptSectionProps {
  receiptImage?: string;
}

const ReceiptSection = ({receiptImage}: ReceiptSectionProps) => {
  if (!receiptImage) return null;

  return (
    <>
      <div className="my-2"></div>
      <span className="mt-2 text-sm font-medium text-gray-700">
        تصویر فیش ارسال
      </span>
      <a
        href={receiptImage}
        target={'_blank'}
        download="image.jpg"
        className="block mt-2 "
      >
        <img
          src={receiptImage}
          alt="receipt preview"
          className="flex items-center justify-center no-underline bg-white border border-gray-600 rounded-lg size-12"
        />
      </a>
    </>
  );
};

// ==================== CONDITIONAL DISPLAY LOGIC ====================

const useDisplayConditions = (props: IProps) => {
  const {
    storeOrderStep,
    isDeliveryInPerson,
    deliveryDate,
    acceptDate,
    tracingCode,
    hasOrderShipped,
    receiptImage,
  } = props;

  // Delivery-in-person specific conditions
  const showInPersonReceiveDate =
    (storeOrderStep === StoreOrderSteps.USER_DELIVERED ||
      storeOrderStep === StoreOrderSteps.COMPLETED) &&
    Boolean(deliveryDate);

  const showInPersonDeliveryDate =
    isDeliveryInPerson &&
    (storeOrderStep === StoreOrderSteps.PROCESSING ||
      storeOrderStep === StoreOrderSteps.STORE_DELIVERED ||
      storeOrderStep === StoreOrderSteps.SHIPPED) &&
    Boolean(deliveryDate);

  // Regular shipping conditions
  const showAcceptDate =
    (storeOrderStep === StoreOrderSteps.SHIPPED ||
      storeOrderStep === StoreOrderSteps.PROCESSING) &&
    Boolean(acceptDate);

  const showApprovedAndDeliveryDates =
    (storeOrderStep === StoreOrderSteps.SHIPPED ||
      storeOrderStep === StoreOrderSteps.PROCESSING) &&
    !isDeliveryInPerson;

  const showTrackingCode =
    Boolean(tracingCode) &&
    hasOrderShipped &&
    (storeOrderStep === StoreOrderSteps.SHIPPED ||
      storeOrderStep === StoreOrderSteps.STORE_DELIVERED);

  const showDeliveryMethod =
    Boolean(props.storeDeliveryMethod) &&
    hasOrderShipped &&
    (storeOrderStep === StoreOrderSteps.SHIPPED ||
      storeOrderStep === StoreOrderSteps.STORE_DELIVERED ||
      storeOrderStep === StoreOrderSteps.COMPLETED);

  const showReceipt =
    Boolean(deliveryDate) &&
    hasOrderShipped &&
    (storeOrderStep === StoreOrderSteps.SHIPPED ||
      storeOrderStep === StoreOrderSteps.STORE_DELIVERED) &&
    Boolean(receiptImage);

  return {
    showInPersonReceiveDate,
    showInPersonDeliveryDate,
    showAcceptDate,
    showApprovedAndDeliveryDates,
    showTrackingCode,
    showDeliveryMethod,
    showReceipt,
  };
};

// ==================== MAIN COMPONENT ====================

const OrderSupplyingStatus = (props: IProps) => {
  const deliveryMethodText = getDeliveryMethodText(props.storeDeliveryMethod);
  const statusDisplay = getStatusDisplay(props.storeOrderStep);
  const isOrderIncomplete =
    getStatusDisplay(props.storeOrderStep) === 'تکمیل نشده';

  const {
    showInPersonReceiveDate,
    showInPersonDeliveryDate,
    showAcceptDate,
    showApprovedAndDeliveryDates,
    showTrackingCode,
    showDeliveryMethod,
    showReceipt,
  } = useDisplayConditions(props);

  return (
    <div className="space-y-2">
      <span className="text-sm font-medium text-zinc-500">
        مراحل تامین سفارش
      </span>

      <div className="p-3 space-y-3 bg-zinc-100 rounded-xl">
        {/* Always show status */}
        <InfoRow
          icon="icon-namereport1"
          className={isOrderIncomplete ? ' text-red-600' : ''}
          labelClassName={isOrderIncomplete ? ' text-red-600' : ''}
          label="وضعیت سفارش"
          value={statusDisplay}
        />

        {/* In-person delivery specific sections */}
        <ConditionalInfoRow
          condition={showInPersonReceiveDate}
          icon="icon-nameCalendar"
          label="تاریخ دریافت"
          value={props.deliveryDate}
        />

        <ConditionalInfoRow
          condition={showInPersonDeliveryDate}
          icon="icon-nameCalendar"
          label="تاریخ تحویل سفارش"
          value={props.deliveryDate}
        />

        {/* Regular order acceptance date */}
        <ConditionalInfoRow
          condition={showAcceptDate}
          icon="icon-nameCalendar"
          label="تاریخ تایید سفارش"
          value={props.acceptDate}
        />

        {/* Approval and delivery dates (for shipped orders) */}
        {showApprovedAndDeliveryDates && (
          <>
            <ConditionalInfoRow
              condition={Boolean(props.approved_at)}
              icon="icon-nameCalendar"
              label="تاریخ تایید سفارش"
              value={props.approved_at}
            />

            <ConditionalInfoRow
              condition={Boolean(props.deliveryDate)}
              icon="icon-nameCalendar"
              label="تاریخ ارسال"
              value={props.deliveryDate}
            />
          </>
        )}

        {/* Tracking code */}
        <ConditionalInfoRow
          condition={showTrackingCode}
          icon="icon-nameCalendar"
          label="کد رهگیری مرسوله"
          value={props.tracingCode}
        />

        {/* Delivery method */}
        {showDeliveryMethod && (
          <InfoRow
            icon="icon-truck"
            label="نوع ارسال"
            value={deliveryMethodText}
          />
        )}

        {/* Receipt section */}
        {showReceipt && <ReceiptSection receiptImage={props.receiptImage} />}
      </div>
    </div>
  );
};

export default OrderSupplyingStatus;
